/**
 * CronosLottoTab — extracted from CronosMonitor.tsx (Sprint 3 cleanup).
 *
 * The "Délégation + Lotto" tab content. Standalone component so the
 * parent CronosMonitor can be migrated to the shared monitor primitives
 * (page header + tabs + add-form). The data flow is unchanged: takes
 * the wallet addresses + aggregated totalStaked + croPrice from the
 * staking layer and renders the lotto UX (simulator, early-bird VIP
 * list, waitlist signup, no-loss explainer).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../Icons';
import { LOTTO_CONFIG } from '../../types/cronos.types';

const EARLY_BIRD_STORAGE_KEY = 'cronos_early_bird_supporters';

// Initial Early Bird supporters (hardcoded). Extra signups merged at
// runtime from localStorage.
const INITIAL_EARLY_BIRD = [
  { address: 'cro1q8...7m4k', amount: 350000, date: '2024-12-10' },
  { address: 'cro1x2...9p3j', amount: 250000, date: '2024-12-12' },
  { address: 'cro1k7...2w8n', amount: 150000, date: '2024-12-14' },
  { address: 'cro1m4...6v1h', amount: 50000, date: '2024-12-15' },
];

interface EarlyBirdSupporter {
  address: string;
  amount: number;
  date: string;
}

// Inlined from CronosMonitor.tsx (the only 3 styles LottoTab uses).
const cardStyle: React.CSSProperties = {
  background: 'rgba(0, 0, 0, 0.25)',
  backdropFilter: 'blur(20px)',
  borderRadius: '16px',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  padding: '20px',
  marginBottom: '20px',
};
const cardTitleStyle: React.CSSProperties = {
  fontSize: '15px',
  fontWeight: 600,
  marginBottom: '16px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};
const buttonStyle: React.CSSProperties = {
  padding: '12px 20px',
  background: 'linear-gradient(135deg, #002D74, #1199FA)',
  color: 'white',
  border: 'none',
  borderRadius: '10px',
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  transition: 'all 0.2s',
};

export function CronosLottoTab({
  wallets,
  totalStaked,
  croPrice,
}: {
  wallets: string[];
  totalStaked: number;
  croPrice: number;
}) {
  const { t, i18n } = useTranslation();
  const [simulationAmount, setSimulationAmount] = useState<number>(50000);

  // Masternode waitlist form state
  const [waitlistEmail, setWaitlistEmail] = useState('');
  // Auto-populate wallet addresses from Staking tab
  const [waitlistWallet, setWaitlistWallet] = useState(() => wallets.join(', '));
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [waitlistSuccess, setWaitlistSuccess] = useState(false);
  const [waitlistError, setWaitlistError] = useState('');

  // Early Bird supporters from localStorage + initial list
  const [earlyBirdSupporters, setEarlyBirdSupporters] = useState<EarlyBirdSupporter[]>(() => {
    try {
      const saved = localStorage.getItem(EARLY_BIRD_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved) as EarlyBirdSupporter[];
        // Merge with initial list, avoiding duplicates
        const allSupporters = [...INITIAL_EARLY_BIRD];
        parsed.forEach(p => {
          if (!allSupporters.some(s => s.address === p.address)) {
            allSupporters.push(p);
          }
        });
        return allSupporters;
      }
      return INITIAL_EARLY_BIRD;
    } catch {
      return INITIAL_EARLY_BIRD;
    }
  });

  // Masternode progress
  const masternodeGoal = 3500000;
  const masternodeCurrent = earlyBirdSupporters.reduce((sum, s) => sum + s.amount, 0);
  const masternodePercent = Math.round((masternodeCurrent / masternodeGoal) * 100);

  // Handle waitlist submission
  const handleWaitlistSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!waitlistEmail) return;

    setWaitlistLoading(true);
    setWaitlistError('');

    try {
      const response = await fetch('/api/cronos/masternode-waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: waitlistEmail,
          wallet: waitlistWallet || undefined,
          language: i18n.language?.substring(0, 2) || 'fr',
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to subscribe');
      }

      // Add user to Early Bird list in localStorage
      if (waitlistWallet) {
        const shortAddress = waitlistWallet.length > 12
          ? `${waitlistWallet.slice(0, 6)}...${waitlistWallet.slice(-4)}`
          : waitlistWallet;
        const today = new Date().toISOString().split('T')[0];
        const newSupporter: EarlyBirdSupporter = {
          address: shortAddress,
          amount: 10000, // Default amount for new Early Birds
          date: today,
        };

        // Update state
        setEarlyBirdSupporters(prev => {
          // Avoid duplicates
          if (prev.some(s => s.address === shortAddress)) {
            return prev;
          }
          const updated = [...prev, newSupporter];
          // Save new supporters to localStorage (only the ones not in INITIAL_EARLY_BIRD)
          const toSave = updated.filter(s => !INITIAL_EARLY_BIRD.some(i => i.address === s.address));
          localStorage.setItem(EARLY_BIRD_STORAGE_KEY, JSON.stringify(toSave));
          return updated;
        });
      }

      setWaitlistSuccess(true);
      setWaitlistEmail('');
      setWaitlistWallet('');
    } catch {
      setWaitlistError(t('cronos.masternode.error', 'Une erreur est survenue. Veuillez réessayer.'));
    } finally {
      setWaitlistLoading(false);
    }
  };

  // Config
  const dailyPrizePercent = 30; // 30% of daily staking gains distributed
  const winnersPerDay = 50;
  const minStake = 10000; // Minimum CRO to participate
  const apy = 7.44;
  const chances = totalStaked >= minStake ? Math.floor(totalStaked / minStake) : 0;

  // Simulation calculations
  const simChances = Math.floor(simulationAmount / minStake);
  const simDailyStakingReward = (simulationAmount * (apy / 100)) / 365;
  const simMonthlyStakingReward = simDailyStakingReward * 30;
  // Was: simPotentialLottoGain (computed but never displayed). Removed
  // dead code.

  return (
    <div>
      {/* Hero Banner - Compact */}
      <div
        className="lotto-hero"
        style={{
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15), rgba(118, 75, 162, 0.1))',
          border: '1px solid rgba(102, 126, 234, 0.25)',
          borderRadius: '16px',
          padding: '20px 24px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '16px',
        }}
      >
        <div className="lotto-hero-content" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <img
            src="/loaded-lion.svg"
            alt="Loaded Lion"
            style={{
              width: '80px',
              height: '80px',
              borderRadius: '12px',
              boxShadow: '0 4px 15px rgba(255, 215, 0, 0.3)',
            }}
          />
          <div>
            <h2 style={{ fontSize: '22px', fontWeight: 700, margin: 0 }}>
              {t('cronos.lotto.delegateAndWin', 'Delegate & Win')}
            </h2>
            <p style={{ fontSize: '13px', color: 'var(--muted)', margin: '4px 0 0 0', maxWidth: '400px' }}>
              {t('cronos.lotto.simpleExplanation', 'Delegate your CRO with our validator and automatically participate in the daily lottery. Every day, random delegators win free CRO!')}
            </p>
          </div>
        </div>

        {/* Key stats */}
        <div className="lotto-hero-stats" style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{
            padding: '12px 20px',
            background: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '10px',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '24px', fontWeight: 700, color: '#FFD700' }}>
              {dailyPrizePercent}%
            </div>
            <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
              {t('cronos.lotto.dailyGains', 'of daily gains')}
            </div>
          </div>
          <div style={{
            padding: '12px 20px',
            background: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '10px',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '24px', fontWeight: 700, color: '#10b981' }}>
              {winnersPerDay}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
              {t('cronos.lotto.winnersPerDay', 'winners per day')}
            </div>
          </div>
        </div>
      </div>

      {/* Two columns */}
      <div className="lotto-two-columns" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>

        {/* LEFT: Your participation */}
        <div style={cardStyle}>
          <h3 style={{ ...cardTitleStyle, fontSize: '18px' }}>
            <span style={{ fontSize: '20px' }}>🎲</span>
            {t('cronos.lotto.yourParticipation', 'Your Participation')}
          </h3>

          {/* User's actual chances if they have stake */}
          {totalStaked >= minStake && (
            <div style={{
              padding: '20px',
              background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.1), rgba(255, 165, 0, 0.05))',
              border: '1px solid rgba(255, 215, 0, 0.2)',
              borderRadius: '12px',
              textAlign: 'center',
              marginBottom: '16px',
            }}>
              <div style={{ fontSize: '14px', color: 'var(--muted)', marginBottom: '8px' }}>
                {t('cronos.lotto.yourChances', 'Your daily chances')}
              </div>
              <div
                className="lotto-chances-number"
                style={{
                  fontSize: '48px',
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #FFD700, #FFA500)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {chances}x
              </div>
              <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                {t('cronos.lotto.basedOnStake', '(1 chance per {{amount}} CRO staked)', { amount: minStake.toLocaleString() })}
              </div>
            </div>
          )}

          {/* Simulator Section */}
          <div style={{
            padding: '16px',
            background: 'rgba(102, 126, 234, 0.1)',
            border: '1px solid rgba(102, 126, 234, 0.2)',
            borderRadius: '12px',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '12px',
              fontSize: '14px',
              fontWeight: 600,
              color: '#667eea',
            }}>
              <span>🔮</span>
              {t('cronos.lotto.simulator', 'Simulateur de gains')}
            </div>

            {/* Amount slider */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px',
              }}>
                <span style={{ fontSize: '12px', color: 'var(--muted)' }}>
                  {t('cronos.lotto.simulateAmount', 'Montant à déléguer')}
                </span>
                <span style={{
                  fontSize: '14px',
                  fontWeight: 700,
                  color: '#667eea',
                }}>
                  {simulationAmount.toLocaleString()} CRO
                </span>
              </div>
              <input
                type="range"
                min={minStake}
                max={500000}
                step={10000}
                value={simulationAmount}
                onChange={(e) => setSimulationAmount(Number(e.target.value))}
                style={{
                  width: '100%',
                  height: '6px',
                  borderRadius: '3px',
                  background: `linear-gradient(to right, #667eea 0%, #667eea ${((simulationAmount - minStake) / (500000 - minStake)) * 100}%, rgba(255,255,255,0.1) ${((simulationAmount - minStake) / (500000 - minStake)) * 100}%, rgba(255,255,255,0.1) 100%)`,
                  appearance: 'none',
                  cursor: 'pointer',
                }}
              />
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '10px',
                color: 'var(--muted)',
                marginTop: '4px',
              }}>
                <span>{minStake.toLocaleString()}</span>
                <span>500,000</span>
              </div>
            </div>

            {/* Simulation results */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '10px',
            }}>
              {/* Chances */}
              <div style={{
                padding: '12px',
                background: 'rgba(255, 215, 0, 0.1)',
                borderRadius: '8px',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: '10px', color: 'var(--muted)', marginBottom: '4px' }}>
                  {t('cronos.lotto.simChances', 'Vos chances')}
                </div>
                <div style={{
                  fontSize: '24px',
                  fontWeight: 700,
                  color: '#FFD700',
                }}>
                  {simChances}x
                </div>
                <div style={{ fontSize: '9px', color: 'var(--muted)' }}>
                  {t('cronos.lotto.perDay', '/jour')}
                </div>
              </div>

              {/* Staking rewards */}
              <div style={{
                padding: '12px',
                background: 'rgba(16, 185, 129, 0.1)',
                borderRadius: '8px',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: '10px', color: 'var(--muted)', marginBottom: '4px' }}>
                  {t('cronos.lotto.simStakingReward', 'Rewards staking')}
                </div>
                <div style={{
                  fontSize: '18px',
                  fontWeight: 700,
                  color: '#10b981',
                }}>
                  ~{simMonthlyStakingReward.toFixed(0)} CRO
                </div>
                <div style={{ fontSize: '9px', color: 'var(--muted)' }}>
                  {t('cronos.lotto.perMonth', '/mois')}
                </div>
              </div>
            </div>

            {/* Lotto bonus potential */}
            <div style={{
              marginTop: '10px',
              padding: '12px',
              background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.15), rgba(255, 165, 0, 0.1))',
              borderRadius: '8px',
              textAlign: 'center',
            }}>
              <div style={{ fontSize: '10px', color: 'var(--muted)', marginBottom: '4px' }}>
                {t('cronos.lotto.simLottoBonus', 'Bonus Lotto potentiel')}
              </div>
              <div style={{
                fontSize: '20px',
                fontWeight: 700,
                background: 'linear-gradient(135deg, #FFD700, #FFA500)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}>
                +{(simMonthlyStakingReward * (dailyPrizePercent / 100)).toFixed(0)} CRO
              </div>
              <div style={{ fontSize: '9px', color: 'var(--muted)' }}>
                {t('cronos.lotto.ifWinner', 'si gagnant ({{percent}}% des gains)', { percent: dailyPrizePercent })}
              </div>
            </div>

            {/* Total potential */}
            <div style={{
              marginTop: '12px',
              padding: '10px',
              background: 'rgba(0, 0, 0, 0.2)',
              borderRadius: '8px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <span style={{ fontSize: '12px', color: 'var(--muted)' }}>
                {t('cronos.lotto.simTotalPotential', 'Total potentiel/mois')}
              </span>
              <span style={{
                fontSize: '16px',
                fontWeight: 700,
                color: '#10b981',
              }}>
                ~{(simMonthlyStakingReward * (1 + dailyPrizePercent / 100)).toFixed(0)} CRO
              </span>
            </div>

            {/* USD equivalent */}
            {croPrice > 0 && (
              <div style={{
                marginTop: '8px',
                textAlign: 'center',
                fontSize: '11px',
                color: 'var(--muted)',
              }}>
                ≈ ${(simMonthlyStakingReward * (1 + dailyPrizePercent / 100) * croPrice).toFixed(2)} USD/mois
              </div>
            )}
          </div>

          {/* Early Bird Section */}
          <div style={{
            marginTop: '20px',
            padding: '16px',
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(236, 72, 153, 0.05))',
            border: '1px solid rgba(139, 92, 246, 0.2)',
            borderRadius: '12px',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '12px',
            }}>
              <span style={{ fontSize: '18px' }}>🐣</span>
              <span style={{ fontSize: '14px', fontWeight: 600, color: '#a78bfa' }}>
                {t('cronos.masternode.earlyBird', 'Early Bird Supporters')}
              </span>
              <span style={{
                marginLeft: 'auto',
                padding: '2px 8px',
                background: 'rgba(139, 92, 246, 0.2)',
                borderRadius: '10px',
                fontSize: '10px',
                color: '#a78bfa',
                fontWeight: 600,
              }}>
                VIP
              </span>
            </div>

            <div style={{ display: 'grid', gap: '8px' }}>
              {earlyBirdSupporters.map((supporter, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px 12px',
                    background: 'rgba(0, 0, 0, 0.2)',
                    borderRadius: '8px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '6px',
                      background: 'linear-gradient(135deg, #a78bfa, #ec4899)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                    }}>
                      👑
                    </div>
                    <div>
                      <div style={{ fontFamily: 'monospace', fontSize: '11px', color: 'var(--text)' }}>
                        {supporter.address}
                      </div>
                      <div style={{ fontSize: '9px', color: 'var(--muted)' }}>
                        {supporter.date}
                      </div>
                    </div>
                  </div>
                  <div style={{
                    padding: '4px 10px',
                    background: 'rgba(139, 92, 246, 0.15)',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#a78bfa',
                  }}>
                    {supporter.amount.toLocaleString()} CRO
                  </div>
                </div>
              ))}
            </div>

            <div style={{
              marginTop: '12px',
              padding: '10px',
              background: 'rgba(0, 0, 0, 0.2)',
              borderRadius: '8px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <span style={{ fontSize: '12px', color: 'var(--muted)' }}>
                {t('cronos.masternode.totalPledged', 'Total engagé')}
              </span>
              <span style={{
                fontSize: '14px',
                fontWeight: 700,
                color: '#a78bfa',
              }}>
                {masternodeCurrent.toLocaleString()} CRO
              </span>
            </div>
          </div>
        </div>

        {/* RIGHT: Delegate section */}
        <div style={cardStyle}>
          <h3 style={{ ...cardTitleStyle, fontSize: '18px' }}>
            <Icons.Wallet size={20} />
            {t('cronos.lotto.delegateTitle', 'Delegate your CRO')}
          </h3>

          <div style={{
            padding: '20px',
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(6, 182, 212, 0.05))',
            borderRadius: '12px',
            marginBottom: '20px',
          }}>
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              <div style={{ fontSize: '14px', color: 'var(--muted)', marginBottom: '4px' }}>
                {t('cronos.lotto.ourValidator', 'Our Validator')}
              </div>
              <div style={{ fontSize: '20px', fontWeight: 700, color: '#10b981' }}>
                {LOTTO_CONFIG.validatorMoniker}
              </div>
              {/* Hetzner Partner Badge */}
              <div style={{
                marginTop: '8px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 10px',
                background: 'rgba(213, 0, 0, 0.1)',
                border: '1px solid rgba(213, 0, 0, 0.2)',
                borderRadius: '6px',
                fontSize: '10px',
                color: 'var(--muted)',
              }}>
                <span style={{ color: '#d50000', fontWeight: 700 }}>HETZNER</span>
                <span>•</span>
                <span>{t('cronos.lotto.hostedOn', 'Infrastructure')}</span>
              </div>
            </div>

            <div
              className="lotto-validator-stats"
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '12px',
                marginBottom: '16px',
              }}
            >
              <div style={{ textAlign: 'center', padding: '12px', background: 'rgba(0,0,0,0.2)', borderRadius: '8px' }}>
                <div style={{ fontSize: '11px', color: 'var(--muted)' }}>Commission</div>
                <div style={{ fontSize: '16px', fontWeight: 600 }}>5%</div>
              </div>
              <div style={{ textAlign: 'center', padding: '12px', background: 'rgba(0,0,0,0.2)', borderRadius: '8px' }}>
                <div style={{ fontSize: '11px', color: 'var(--muted)' }}>APY</div>
                <div style={{ fontSize: '16px', fontWeight: 600, color: '#10b981' }}>~7.44%</div>
              </div>
            </div>

            {/* Disabled delegation button */}
            <button
              disabled
              style={{
                ...buttonStyle,
                width: '100%',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #374151, #1f2937)',
                padding: '14px',
                fontSize: '15px',
                opacity: 0.6,
                cursor: 'not-allowed',
              }}
            >
              <Icons.ExternalLink size={18} />
              {t('cronos.lotto.delegateNow', 'Delegate with Keplr')}
            </button>
          </div>

          {/* Masternode Development Notice */}
          <div style={{
            padding: '16px',
            background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15), rgba(217, 119, 6, 0.1))',
            border: '1px solid rgba(245, 158, 11, 0.3)',
            borderRadius: '12px',
            marginBottom: '16px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
              <span style={{ fontSize: '20px' }}>🚧</span>
              <span style={{ fontSize: '14px', fontWeight: 600, color: '#f59e0b' }}>
                {t('cronos.masternode.inDevelopment', 'Service en cours de développement')}
              </span>
            </div>
            <p style={{ fontSize: '12px', color: 'var(--muted)', margin: '0 0 16px 0', lineHeight: 1.5 }}>
              {t('cronos.masternode.needCro', 'Pour lancer notre Masternode, nous avons besoin de collecter {{goal}} CRO.', { goal: masternodeGoal.toLocaleString() })}
            </p>

            {/* Progress bar */}
            <div style={{ marginBottom: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--muted)', marginBottom: '6px' }}>
                <span>{masternodeCurrent.toLocaleString()} CRO</span>
                <span>{masternodeGoal.toLocaleString()} CRO</span>
              </div>
              <div style={{
                height: '12px',
                background: 'rgba(0,0,0,0.3)',
                borderRadius: '6px',
                overflow: 'hidden',
              }}>
                <div style={{
                  width: `${masternodePercent}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #f59e0b, #d97706)',
                  borderRadius: '6px',
                  transition: 'width 0.5s ease',
                }} />
              </div>
              <div style={{ textAlign: 'center', marginTop: '6px' }}>
                <span style={{ fontSize: '14px', fontWeight: 700, color: '#f59e0b' }}>{masternodePercent}%</span>
                <span style={{ fontSize: '11px', color: 'var(--muted)', marginLeft: '6px' }}>
                  {t('cronos.masternode.completed', 'complété')}
                </span>
              </div>
            </div>
          </div>

          {/* Waitlist Signup Form */}
          <div style={{
            padding: '16px',
            background: 'rgba(102, 126, 234, 0.1)',
            border: '1px solid rgba(102, 126, 234, 0.2)',
            borderRadius: '12px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
              <span style={{ fontSize: '18px' }}>📧</span>
              <span style={{ fontSize: '14px', fontWeight: 600, color: '#667eea' }}>
                {t('cronos.masternode.joinWaitlist', 'Rejoignez la liste d\'attente')}
              </span>
            </div>

            {waitlistSuccess ? (
              <div style={{
                padding: '16px',
                background: 'rgba(16, 185, 129, 0.15)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: '8px',
                textAlign: 'center',
              }}>
                <span style={{ fontSize: '24px' }}>✅</span>
                <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#10b981', fontWeight: 600 }}>
                  {t('cronos.masternode.subscribed', 'Merci ! Vous serez notifié dès le lancement.')}
                </p>
              </div>
            ) : (
              <form onSubmit={handleWaitlistSubmit}>
                <p style={{ fontSize: '12px', color: 'var(--muted)', margin: '0 0 12px 0' }}>
                  {t('cronos.masternode.helpUs', 'Voulez-vous nous aider à lancer ce service ?')}
                </p>

                <div style={{ marginBottom: '10px' }}>
                  <input
                    type="email"
                    placeholder={t('cronos.masternode.emailPlaceholder', 'Votre email *')}
                    value={waitlistEmail}
                    onChange={(e) => setWaitlistEmail(e.target.value)}
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      background: 'rgba(0,0,0,0.3)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px',
                      color: 'var(--foreground)',
                      fontSize: '13px',
                    }}
                  />
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <input
                    type="text"
                    placeholder={t('cronos.masternode.walletPlaceholder', 'Votre adresse wallet CRO (optionnel)')}
                    value={waitlistWallet}
                    onChange={(e) => setWaitlistWallet(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      background: 'rgba(0,0,0,0.3)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px',
                      color: 'var(--foreground)',
                      fontSize: '13px',
                    }}
                  />
                  <p style={{ fontSize: '10px', color: 'var(--muted)', margin: '4px 0 0 4px' }}>
                    {t('cronos.masternode.walletHelp', 'Pour estimer votre délégation future')}
                  </p>
                </div>

                {waitlistError && (
                  <p style={{ fontSize: '12px', color: '#ef4444', margin: '0 0 12px 0' }}>
                    {waitlistError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={waitlistLoading || !waitlistEmail}
                  style={{
                    ...buttonStyle,
                    width: '100%',
                    justifyContent: 'center',
                    background: waitlistLoading ? '#374151' : 'linear-gradient(135deg, #667eea, #764ba2)',
                    padding: '12px',
                    fontSize: '14px',
                    opacity: waitlistLoading || !waitlistEmail ? 0.6 : 1,
                    cursor: waitlistLoading || !waitlistEmail ? 'not-allowed' : 'pointer',
                  }}
                >
                  {waitlistLoading ? (
                    <span>{t('cronos.masternode.sending', 'Envoi...')}</span>
                  ) : (
                    <>
                      <Icons.Send size={16} />
                      {t('cronos.masternode.submit', 'M\'inscrire')}
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* How the lottery system works - Detailed explanation */}
      <div style={{ ...cardStyle, marginTop: '24px' }}>
        <h3 style={{ ...cardTitleStyle, fontSize: '18px' }}>
          <span style={{ fontSize: '20px' }}>📖</span>
          {t('cronos.lotto.howItWorks', 'How it works')}
        </h3>

        <div className="lotto-three-columns" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
          {/* Step 1 */}
          <div style={{
            padding: '20px',
            background: 'rgba(102, 126, 234, 0.1)',
            borderRadius: '12px',
            textAlign: 'center',
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 12px',
              fontSize: '18px',
              fontWeight: 700,
              color: 'white',
            }}>1</div>
            <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>
              {t('cronos.lotto.step1Title', 'Delegate')}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
              {t('cronos.lotto.step1Desc', 'Delegate at least {{amount}} CRO to our validator via Keplr or any Cosmos wallet', { amount: minStake.toLocaleString() })}
            </div>
          </div>

          {/* Step 2 */}
          <div style={{
            padding: '20px',
            background: 'rgba(16, 185, 129, 0.1)',
            borderRadius: '12px',
            textAlign: 'center',
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10b981, #059669)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 12px',
              fontSize: '18px',
              fontWeight: 700,
              color: 'white',
            }}>2</div>
            <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>
              {t('cronos.lotto.step2Title', 'Daily Draw')}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
              {t('cronos.lotto.step2Desc', 'Every day, {{percent}}% of staking rewards are distributed to {{winners}} random delegators', { percent: dailyPrizePercent, winners: winnersPerDay })}
            </div>
          </div>

          {/* Step 3 */}
          <div style={{
            padding: '20px',
            background: 'rgba(255, 215, 0, 0.1)',
            borderRadius: '12px',
            textAlign: 'center',
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #FFD700, #FFA500)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 12px',
              fontSize: '18px',
              fontWeight: 700,
              color: 'white',
            }}>3</div>
            <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>
              {t('cronos.lotto.step3Title', 'Win CRO')}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
              {t('cronos.lotto.step3Desc', 'Winners receive CRO automatically. More you stake = more chances to win!')}
            </div>
          </div>
        </div>

        {/* Additional info */}
        <div style={{
          marginTop: '20px',
          padding: '16px',
          background: 'rgba(255, 255, 255, 0.03)',
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
        }}>
          <Icons.Info size={18} color="#667eea" style={{ flexShrink: 0, marginTop: '2px' }} />
          <div style={{ fontSize: '13px', color: 'var(--muted)', lineHeight: 1.5 }}>
            {t('cronos.lotto.systemExplanation', 'The lottery is funded by a portion of the validator commission. Your staked CRO continues to earn normal staking rewards (~7.44% APY) regardless of lottery results. The more CRO you delegate, the more chances you have to win each day.')}
          </div>
        </div>
      </div>

      {/* No-loss reminder */}
      <div
        className="lotto-no-loss"
        style={{
          marginTop: '24px',
          padding: '16px 20px',
          background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.1), rgba(255, 165, 0, 0.05))',
          border: '1px solid rgba(255, 215, 0, 0.2)',
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
        }}
      >
        <div style={{ fontSize: '32px' }}>✨</div>
        <div>
          <div style={{ fontSize: '14px', fontWeight: 600, color: '#FFD700', marginBottom: '4px' }}>
            {t('cronos.lotto.noLoss', 'No-Loss Lottery')}
          </div>
          <div style={{ fontSize: '13px', color: 'var(--muted)' }}>
            {t('cronos.lotto.noLossSimple', 'You keep earning normal staking rewards (~7.44% APY) even if you never win. Your CRO stays safely staked!')}
          </div>
        </div>
      </div>

      {/* Responsive styles for mobile */}
      <style>{`
        @media (max-width: 768px) {
          /* Stack 2-column grids */
          .lotto-two-columns {
            grid-template-columns: 1fr !important;
          }
          /* Stack 3-column grids */
          .lotto-three-columns {
            grid-template-columns: 1fr !important;
          }
          /* Hero banner adjustments */
          .lotto-hero {
            padding: 16px !important;
            flex-direction: column !important;
            align-items: flex-start !important;
          }
          .lotto-hero-content {
            flex-direction: column !important;
            text-align: center !important;
            width: 100% !important;
          }
          .lotto-hero-content img {
            width: 60px !important;
            height: 60px !important;
            margin: 0 auto !important;
          }
          .lotto-hero-content > div {
            text-align: center !important;
          }
          .lotto-hero-stats {
            width: 100% !important;
            justify-content: center !important;
          }
          /* Winner item responsive */
          .lotto-winner-item {
            flex-direction: column !important;
            gap: 8px !important;
            text-align: center !important;
          }
          .lotto-winner-item > div:first-child {
            justify-content: center !important;
          }
          /* Smaller fonts on mobile */
          .lotto-chances-number {
            font-size: 36px !important;
          }
          /* No-loss banner */
          .lotto-no-loss {
            flex-direction: column !important;
            text-align: center !important;
          }
          .lotto-no-loss > div:first-child {
            margin-bottom: 8px;
          }
        }

        @media (max-width: 480px) {
          .lotto-hero-stats {
            flex-direction: column !important;
            gap: 8px !important;
          }
          .lotto-hero-stats > div {
            width: 100% !important;
          }
          .lotto-validator-stats {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}
